import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Notification,
  useNotify,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { ShowData } from "./transferRewardAmount";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  card: {
    minWidth: 300,
    maxWidth: 600,
    marginTop: "6em",
  },
  avatar: {
    margin: "1em",
    display: "flex",
    justifyContent: "center",
  },
  form: {
    padding: "0 0.5em 0.5em 0.5em",
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  search: {
    padding: "10px",
    margin: "16px",
  },
  inputContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  select: {
    paddingLeft: "0.5em",
    minWidth: "120px",
  },
  table: {
    minWidth: 650,
    margin: "20px 0",
  },
  tableContainer: {
    margin: "20px 0",
  },
  updateForm: {
    padding: "15px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#f0f8ff",
    marginTop: "15px",
    maxWidth: "500px",
    margin: "15px auto 0 auto",
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  style,
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    style={style}
  />
);

interface FormValues {
  email?: string;
  field?: string;
  value?: string;
  dob?: string;
}

const { Form } = withTypes<FormValues>();

const UpdateUser = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [userData, setUserData] = useState<any[]>([]);
  const [selectedField, setSelectedField] = useState<string>('');
  const [showNameWarning, setShowNameWarning] = useState(false);

  const fieldOptions = [
    { value: 'email', label: 'Email' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
  ];

  const handleSearch = (inputData: any) => {
    setLoading(true);
    setUserData([]);
    notify("Searching for user...");

    const { email } = inputData;

    if (!email) {
      notify("Please enter an email address", "error", "", undefined, 5000);
      setLoading(false);
      return;
    }

    let config = getConfigGet({
      email: email
    });

    axios
      .get(ENDPOINTS.UPDATE_USER, config)
      .then((res: any) => {
        setLoading(false);
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          setUserData(res.data);
          notify(`User found: ${res.data[1][1]}`, "info", "", undefined, 5000);
        } else {
          notify("User not found", "error", "", undefined, 5000);
        }
      })
      .catch((error) => {
        console.log("error", error);
        setLoading(false);
        notify(
          error?.response?.data?.message || "Failed to search for user",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleUpdate = (inputData: any) => {
    console.log("handleUpdate called with:", inputData);
    
    if (!userData || userData.length === 0) {
      notify("Please search for a user first", "error", "", undefined, 5000);
      return;
    }

    const { field, value } = inputData;
    
    // Check if new value is different from current value
    if (field && value && userData && userData.length > 1) {
      const fieldIndex = ['id', 'email', 'first_name', 'last_name'].indexOf(field);
      const currentValue = userData[1][fieldIndex] || '';
      
      if (value.trim() === currentValue.trim()) {
        notify("New value must be different from current value", "error", "", undefined, 5000);
        return;
      }
    }

    setUpdateLoading(true);
    notify("Updating user...");

    const data = {
      field: field,
      value: value
    };

    const config = {
      headers: getConfigPost(),
      params: {
        email: userData[1][1] // userData[1] is the user row, [1] is the email column
      }
    };

    axios
      .patch(ENDPOINTS.UPDATE_USER, data, config)
      .then((res: any) => {
        setUpdateLoading(false);
        console.log("res", res);
        if (res?.data?.success) {
          // Use formatted data from backend (headers are now managed by backend)
          if (res.data.formattedData) {
            setUserData(res.data.formattedData);
          }
          notify(`${field} updated successfully`, "info", "", undefined, 5000);
          // Reset form
          setSelectedField('');
        } else {
          notify("Failed to update user", "error", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setUpdateLoading(false);
        notify(
          error?.response?.data?.message || "Failed to update user",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email) {
      errors.email = "Email is required";
    }
    return errors;
  };

  const validateUpdate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.field) {
      errors.field = "Field selection is required";
    }
    if (!values.value) {
      errors.value = "Value is required";
    }
    
    // Validation moved to handleUpdate function to avoid race conditions
    
    return errors;
  };

  return (
    <>
      <Form
        onSubmit={handleSearch}
        validate={validate}
        render={({ handleSubmit, form }) => (
          <div>
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.main}>
                <div style={{ 
                  padding: "20px", 
                  border: "1px solid #e0e0e0", 
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  marginBottom: "20px",
                  maxWidth: "600px",
                  margin: "0 auto 20px auto"
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#333" }}>Search User</h3>
                  <div className={classes.form}>
                    <div className={classes.inputContainer}>
                      <Field
                        name="email"
                        component={renderInput}
                        label="Email Address"
                        disabled={loading}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                  <CardActions className={classes.actions}>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ marginRight: '10px', flex: 1 }}
                    >
                      {loading && <CircularProgress size={25} thickness={2} />}
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setUserData([]);
                        setSelectedField('');
                        form.reset();
                      }}
                      style={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                  </CardActions>
                </div>
                <Notification />
              </div>
            </form>

            {/* User Details Table */}
            {userData && userData.length > 0 && (
              <div className={classes.tableContainer} style={{ maxWidth: "600px", margin: "0 auto" }}>
                <ShowData record={userData} tableCenterAlign={true} />
              </div>
            )}

            {/* Update Form */}
            {userData && userData.length > 0 && (
              <Form
                onSubmit={handleUpdate}
                validate={validateUpdate}
                render={({ handleSubmit, form }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.updateForm}>
                      <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>Update</h3>
                      <div className={classes.form} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Field
                          name="field"
                          component={({ input, meta }) => (
                            <FormControl style={{ width: '150px' }}>
                              <InputLabel>Field</InputLabel>
                              <Select
                                {...input}
                                onChange={(e) => {
                                  const field = e.target.value as string;
                                  input.onChange(field);
                                  setSelectedField(field);
                                  // Clear the value field when changing field selection
                                  form.change('value', '');
                                  
                                  // Show notice for name changes
                                  if (field === 'first_name' || field === 'last_name') {
                                    setShowNameWarning(true);
                                  } else {
                                    setShowNameWarning(false);
                                  }
                                }}
                                disabled={updateLoading}
                              >
                                {fieldOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                        <Field
                          name="value"
                          component={renderInput}
                          label={`New ${selectedField ? fieldOptions.find(f => f.value === selectedField)?.label : 'Value'}`}
                          disabled={updateLoading}
                          style={{ width: '150px' }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={updateLoading || !selectedField || !form.getState().values.value}
                          style={{ width: '80px' }}
                          onClick={() => {
                            console.log("Button clicked, form values:", form.getState().values);
                            const formValues = form.getState().values;
                            handleUpdate(formValues);
                          }}
                        >
                          {updateLoading && <CircularProgress size={25} thickness={2} />}
                          Update
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              />
            )}

            {/* Name Warning */}
            {showNameWarning && (
              <div style={{ 
                maxWidth: "500px", 
                margin: "15px auto", 
                padding: "12px", 
                backgroundColor: "#fff3cd", 
                border: "1px solid #ffeaa7", 
                borderRadius: "4px",
                color: "#856404"
              }}>
                <strong>Before updating the name:</strong> If they have already completed onboarding under the previous name, ensure you've fetched a new credit report with the updated name and verify that the same mortgage is available under the new name.
              </div>
            )}

            {/* Separate DOB Update Form */}
            {userData && userData.length > 0 && (
              <Form
                onSubmit={(inputData: any) => {
                  const { dob } = inputData;
                  
                  if (!dob) {
                    notify("Date of birth is required", "error", "", undefined, 5000);
                    return;
                  }

                  // Validate DD/MM/YYYY format
                  const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                  if (!dobRegex.test(dob)) {
                    notify("Date must be in DD/MM/YYYY format", "error", "", undefined, 5000);
                    return;
                  }

                  // Check if new value is different from current value
                  const currentDob = userData[1][4]; // DOB is at index 4
                  if (currentDob) {
                    // Convert current DOB to DD/MM/YYYY for comparison
                    let formattedCurrentDob = currentDob;
                    if (currentDob.includes('-')) {
                      const [year, month, day] = currentDob.split('T')[0].split('-');
                      formattedCurrentDob = `${day}/${month}/${year}`;
                    }
                    if (dob.trim() === formattedCurrentDob.trim()) {
                      notify("New value must be different from current value", "error", "", undefined, 5000);
                      return;
                    }
                  }

                  setUpdateLoading(true);
                  notify("Updating date of birth...");

                  const data = {
                    field: 'dob',
                    value: dob
                  };

                  const config = {
                    headers: getConfigPost(),
                    params: {
                      email: userData[1][1] // userData[1] is the user row, [1] is the email column
                    }
                  };

                  axios
                    .patch(ENDPOINTS.UPDATE_USER, data, config)
                    .then((res: any) => {
                      setUpdateLoading(false);
                      if (res?.data?.success) {
                        if (res.data.formattedData) {
                          setUserData(res.data.formattedData);
                        }
                        notify("Date of birth updated successfully", "info", "", undefined, 5000);
                      } else {
                        notify("Failed to update date of birth", "error", "", undefined, 5000);
                      }
                    })
                    .catch((error) => {
                      setUpdateLoading(false);
                      notify(
                        error?.response?.data?.message || "Failed to update date of birth",
                        "error",
                        "",
                        undefined,
                        5000
                      );
                    });
                }}
                render={({ handleSubmit, form }) => {
                  // Get current DOB and format it for display
                  let currentDobValue = '';
                  if (userData && userData.length > 1) {
                    const dobValue = userData[1][4]; // DOB is at index 4
                    if (dobValue) {
                      if (dobValue.includes('-')) {
                        const [year, month, day] = dobValue.split('T')[0].split('-');
                        currentDobValue = `${day}/${month}/${year}`;
                      } else {
                        currentDobValue = dobValue;
                      }
                    }
                  }

                  return (
                    <form onSubmit={handleSubmit}>
                      <div className={classes.updateForm}>
                        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>Update Date of Birth</h3>
                        <div className={classes.form} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <TextField
                            name="dob"
                            label="(DD/MM/YYYY)"
                            placeholder="DD/MM/YYYY"
                            defaultValue={currentDobValue}
                            disabled={updateLoading}
                            style={{ width: '200px' }}
                            fullWidth
                            onChange={(e) => {
                              let value = e.target.value;
                              const prevValue = form.getState().values.dob || '';
                              
                              // Allow only numbers and forward slashes
                              value = value.replace(/[^0-9/]/g, '');
                              
                              // Limit length to 10 characters (DD/MM/YYYY)
                              if (value.length > 10) {
                                value = value.substring(0, 10);
                              }
                              
                              // Only add slashes if user is typing forward (not deleting)
                              if (value.length > prevValue.length) {
                                // Add slash after day (position 2)
                                if (value.length === 2 && !value.includes('/')) {
                                  value = value + '/';
                                }
                                // Add slash after month (position 5)
                                else if (value.length === 5 && value.split('/').length === 2) {
                                  value = value + '/';
                                }
                              }
                              
                              form.change('dob', value);
                            }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={updateLoading || !form.getState().values.dob}
                            style={{ width: '80px' }}
                            type="submit"
                          >
                            {updateLoading && <CircularProgress size={25} thickness={2} />}
                            Update
                          </Button>
                        </div>
                      </div>
                    </form>
                  );
                }}
              />
            )}
          </div>
        )}
      />
    </>
  );
};

export default UpdateUser;
